import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { SnackBar } from '../enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  private readonly snackbar = inject(MatSnackBar);
  public readonly spin = inject(NgxSpinnerService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  openSnackBar(message: string, action: string, type: SnackBar.Error | SnackBar.Success) {
    this.snackbar.open(message, action, {
      duration: 3000,
      verticalPosition: 'bottom',
      panelClass: [`${type}-snackbar`]
    });
  }

  translation(msg: string) {
    return this.translate.instant(msg);
  }

  /** Maps API values like IN-PERSON / VIRTUAL to localized labels from eventForm.labels. */
  translateEventLocationText(locationText: string | null | undefined, emptyFallbackKey: string): string {
    const raw = (locationText || '').trim();
    if (!raw) return this.translation(emptyFallbackKey);
    const upper = raw.toUpperCase().replace(/\s+/g, '-');
    if (upper === 'IN-PERSON') return this.translation('eventForm.labels.inPerson');
    if (upper === 'VIRTUAL') return this.translation('eventForm.labels.virtual');
    return raw;
  }

  toTitleCase(str: string) {
    return (str || '')
      .toLowerCase()
      .split(' ')
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  openConfirmationDialog(data?: any, width?: string): Observable<boolean> {
    const config: MatDialogConfig = {
      data,
      disableClose: true,
      panelClass: 'educare-dialog'
    };
    // If width is provided, set both width and maxWidth.
    if (width) {
      config.width = width;
      config.maxWidth = width;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    return dialogRef.afterClosed();
  }

  /**
   * Method is use to download file.
   * @param data - Array Buffer data
   * @param type - type of the document.
   */
  downLoadFile(data: any, type: string, fileName = '') {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    // Step 3: Create an anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  generateQueryParams(params: any = {}): string {
    const queryParams: string[] = [];

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];
        if (value !== undefined && value !== null) {
          if (typeof value === 'object') {
            // If the value is an object, stringify it as JSON
            queryParams.push(`${key}=${encodeURIComponent(JSON.stringify(value))}`);
          } else {
            // Otherwise, encode the value and append it to the query string
            queryParams.push(`${key}=${encodeURIComponent(value.toString())}`);
          }
        }
      }
    }

    return queryParams.join('&');
  }

  getEncryptedUrls(...args: string[]): string {
    if (!args.length) {
      return '';
    }

    // Only the FIRST argument is treated as a base64-encoded API endpoint.
    // Subsequent arguments are plain URL suffixes (e.g. "/123", "?page=1")
    // and must NEVER be base64-decoded, otherwise short paths like "/107"
    // (which happen to match the base64 alphabet) get decoded into garbage
    // bytes (e.g. "ÿ];") and break the request URL.
    const [first, ...rest] = args;
    const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
    let decodedFirst = first ?? '';
    if (decodedFirst && base64regex.test(decodedFirst) && isNaN(parseFloat(decodedFirst))) {
      try {
        decodedFirst = atob(decodedFirst);
      } catch {
        // If decoding fails for any reason, fall back to the raw value.
      }
    }

    return [decodedFirst, ...rest.map((s) => s ?? '')].join('');
  }

  mimeTypeToExtension(mimeType: string): string | null {
    // Define a mapping of MIME types to file extensions
    const mimeToExtension: Record<string, string> = {
      // Images
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',

      // Documents
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',

      // Spreadsheets
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',

      // Presentations
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',

      // Text
      'text/plain': '.txt',
      'text/csv': '.csv',
      'text/html': '.html',

      // Archives
      'application/zip': '.zip',
      'application/x-rar-compressed': '.rar',
      'application/x-7z-compressed': '.7z',

      // Other
      'application/json': '.json',
      'application/xml': '.xml',
      'text/xml': '.xml'
    };

    // Convert the MIME type to lowercase for case-insensitive comparison
    const lowerCaseMimeType = mimeType?.toLowerCase() || '';

    // Retrieve the corresponding file extension from the mapping
    const extension = mimeToExtension[lowerCaseMimeType];

    // Return the file extension or null if not found
    return extension || null;
  }

  getBlobPath(imageUrl: string = ''): string {
    const splitted = imageUrl?.split?.('/') || '';
    return !!splitted?.length ? splitted[splitted?.length - 1] : '';
  }

  getBlobName(imageUrl: string = ''): string {
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-/;
    const fileName = imageUrl.replace(regex, '');
    return decodeURIComponent(fileName);
  }

  locationReload(): void {
    const url = this.router.url;
    this.router.navigateByUrl('/refresh', { skipLocationChange: true }).then(() => this.router.navigateByUrl(url));
  }

  // MSAL popup is unreliable on mobile browsers (iOS Safari, Android Chrome,
  // in-app browsers) — popup messaging to the opener window is often dropped.
  // Use this to fall back to loginRedirect on mobile.
  isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Date/Time utils
  formatDate(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD');
  }

  formatDateUTC(date: Date): string {
    return dayjs(date).utc().format('YYYY-MM-DD');
  }

  formatTimeUTC(date: Date): string {
    return dayjs(date).utc().format('HH:mm');
  }

  formatTimeToUTC(dateObj: Date): string {
    const utcHours = dateObj.getUTCHours().toString().padStart(2, '0');
    const utcMinutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
    return `${utcHours}:${utcMinutes}`;
  }

  combineDateAndTimeToUTC(date: Date, time: Date | string): Date {
    const { h, m } = this.toHoursMinutes(time);
    return dayjs(date)
      .hour(h || 0)
      .minute(m || 0)
      .second(0)
      .toDate();
  }

  combineDateAndTimeWithOffset(date: Date, time: Date | string): string {
    const { h, m } = this.toHoursMinutes(time);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = isNaN(h) ? 0 : h;
    const minutes = isNaN(m) ? 0 : m;
    const timezoneOffset = this.getTimezoneOffsetString();
    return (
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T` +
      `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00` +
      `${timezoneOffset}`
    );
  }

  getTimezoneOffsetString(): string {
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  utcToLocalTimeOnly(utcStr: string): Date {
    const local = dayjs.utc(utcStr).local();
    return new Date(1970, 0, 1, local.hour(), local.minute(), local.second());
  }

  utcToLocalDatePreservingTime(utcStr: string): Date {
    const local = dayjs.utc(utcStr).local();
    return new Date(local.year(), local.month(), local.date());
  }

  private toHoursMinutes(time: Date | string): { h: number; m: number } {
    if (!time) return { h: NaN, m: NaN };
    if (time instanceof Date) {
      return { h: time.getHours(), m: time.getMinutes() };
    }
    const match = /^(\d{1,2}):(\d{2})/.exec(String(time));
    if (match) return { h: +match[1], m: +match[2] };
    const d = dayjs(time as any);
    if (d.isValid()) return { h: d.hour(), m: d.minute() };
    return { h: NaN, m: NaN };
  }
}
